import { dropTask } from 'ember-concurrency';
import * as v from 'valibot';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import errorsForValibotField from '../../../helpers/errors-for-valibot-field.ts';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import inputmask from '../../../modifiers/inputmask.ts';
import safeFormatNumber from '../../../helpers/safe-format-number.ts';
import ErrorMessages from 'tio-ui/components/error-messages';
import CalculatePrePercentageValue from '../../../utils/tuition-assistance/calculate-pre-percentage-value.ts';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import type Owner from '@ember/owner';
import Button from 'tio-ui/components/button';
import { on } from '@ember/modifier';
import { PhPencil } from 'ember-phosphor-icons';
import { eq, or } from 'tio-ui/utilities';
import { hash } from '@ember/helper';
import type TasApplicationModel from 'tio-data/models/tas-application';
import Divider from 'tio-ui/components/divider';
export interface TuitionAssistanceApplicationDetailsEditableApplicationTotalSignature {
  Args: {
    approvedTotal: number;
    application: TasApplicationModel;
    tasProgramTemplate: TasProgramTemplateModel;
    canEdit: boolean;
    courseCashTotal?: number;
    onSave: (
      approvedTotal: number,
      reason: string,
      approvedCourseCashTotalField?: Record<string, unknown>
    ) => void;
    onCancel?: () => void;
  };
}

const calculatedAppTotalFormSchema = v.object({
  comment: v.pipe(v.string('Comment is required'), v.nonEmpty('Comment is required')),
  total: v.pipe(
    v.number('Total must be a number'),
    v.integer('Total must be an integer'),
    v.minValue(0, 'Total must be at least 0')
  ),
});

export default class TuitionAssistanceApplicationDetailsCalculatedApplicationTotalComponent extends Component<TuitionAssistanceApplicationDetailsEditableApplicationTotalSignature> {
  @tracked prePercentageTotal = 0;
  @tracked prePercentageCourseCashTotal = 0;
  @tracked editableApprovedTotal = 0;
  @tracked editableCourseCashTotal = 0;
  @tracked isEditingApprovedTotal = false;
  @tracked reasonForEdit = '';
  @tracked submitError = null;
  @tracked didSubmit = false;

  constructor(
    owner: Owner,
    args: TuitionAssistanceApplicationDetailsEditableApplicationTotalSignature['Args']
  ) {
    super(owner, args);
    this.syncEditableApprovedTotal();
  }

  get formValidationErrors() {
    const schema = calculatedAppTotalFormSchema;
    const model = {
      comment: this.reasonForEdit,
      total: this.editableApprovedTotal,
    };

    const result = v.safeParse(schema, model);
    if (!result.success) {
      const flattened = v.flatten(result.issues);
      return flattened.nested ?? {};
    }

    return {};
  }

  updateApprovedTotalCalculation = (value: string | number) => {
    const numericValue = Number(value);
    const reimbursementPercentage = this.args.tasProgramTemplate?.reimbursementPercentage;

    if (
      isNaN(numericValue) ||
      reimbursementPercentage === undefined ||
      reimbursementPercentage === null ||
      isNaN(reimbursementPercentage)
    ) {
      this.editableApprovedTotal = 0;
      return;
    }

    const updatedPrePercentageTotal = Math.round(numericValue * 100);
    this.editableApprovedTotal = Math.round(
      updatedPrePercentageTotal * (reimbursementPercentage / 100)
    );
  };

  updateApprovedCourseCashTotalCalculation = (value: string | number) => {
    const numericValue = Number(value);
    const reimbursementPercentage = this.args.tasProgramTemplate?.reimbursementPercentage;

    if (
      isNaN(numericValue) ||
      reimbursementPercentage === undefined ||
      reimbursementPercentage === null ||
      isNaN(reimbursementPercentage)
    ) {
      this.editableCourseCashTotal = 0;
      return;
    }

    const updatedPrePercentageTotal = Math.round(numericValue * 100);
    this.editableCourseCashTotal = Math.round(
      updatedPrePercentageTotal * (reimbursementPercentage / 100)
    );
  };

  toggleIsEditing = () => {
    this.isEditingApprovedTotal = !this.isEditingApprovedTotal;
  };

  cancel = () => {
    this.submitError = null;
    this.isEditingApprovedTotal = false;
    this.didSubmit = false;
    this.syncEditableApprovedTotal();
    this.args.onCancel?.();
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  saveField = dropTask(async () => {
    const additionalFields: Record<string, unknown> = {};

    if (this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH') {
      additionalFields['CARD_FINAL_APPROVED_TOTAL'] = this.editableCourseCashTotal;
    }
    try {
      this.submitError = null;
      this.didSubmit = true;
      await this.args.onSave(this.editableApprovedTotal, this.reasonForEdit, additionalFields);
      this.isEditingApprovedTotal = false;
      this.syncEditableApprovedTotal();
      this.didSubmit = false;
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  get isSaving() {
    return this.saveField.isRunning;
  }

  get isValid() {
    return !!this.reasonForEdit && this.editableApprovedTotal >= 0;
  }

  disabledInput() {
    return;
  }

  syncEditableApprovedTotal() {
    this.editableApprovedTotal = this.args.approvedTotal ?? 0;
    this.reasonForEdit = '';

    const reimbursementPercentage = this.args.tasProgramTemplate?.reimbursementPercentage;

    if (!reimbursementPercentage || isNaN(reimbursementPercentage)) {
      this.prePercentageTotal = 0;
      if (this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH') {
        this.editableCourseCashTotal = this.args.courseCashTotal ?? 0;
        this.prePercentageCourseCashTotal = 0;
      }
      return;
    }

    this.prePercentageTotal = CalculatePrePercentageValue(
      this.editableApprovedTotal,
      reimbursementPercentage
    );

    if (this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH') {
      this.editableCourseCashTotal = this.args.courseCashTotal ?? 0;
      this.prePercentageCourseCashTotal = CalculatePrePercentageValue(
        this.editableCourseCashTotal,
        reimbursementPercentage
      );
    }
  }

  <template>
    <div class="items-center flex gap-2 justify-end text-right">
      {{#if (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")}}
        <div>
          <div class="flex flex-nowrap gap-2">
            <div class="text-sm text-tio-gray-400">
              Course Cash - Tuition:
            </div>
            <div>
              {{safeFormatNumber
                (divide (or @courseCashTotal 0) 100)
                style="currency"
                currency="USD"
              }}
            </div>
          </div>
          <div class="flex flex-nowrap gap-2">
            <div class="text-sm text-tio-gray-400">
              All other expenses:
            </div>
            <div>
              {{safeFormatNumber (divide @approvedTotal 100) style="currency" currency="USD"}}
            </div>
          </div>
        </div>
      {{else}}
        <span class="font-semibold text">
          {{safeFormatNumber (divide @approvedTotal 100) style="currency" currency="USD"}}
        </span>
      {{/if}}
      {{#if @canEdit}}
        <Button {{on "click" this.toggleIsEditing}} title={{t "common.edit"}} class="self-center">
          <PhPencil @size="20" />
        </Button>
      {{/if}}
    </div>

    <Modal
      @isOpen={{this.isEditingApprovedTotal}}
      @onClose={{this.cancel}}
      class="min-h-64"
      @ariaLabel={{t "common.aria_labels.edit_approved_total"}}
    >
      <:header>{{t "tuition_assistance.program_details.status.approved_total"}}</:header>
      <:body>
        <div class="grid grid-cols-2 gap-2">
          <label for="pre-percentage-cc-total" class="font-semibold">
            {{@tasProgramTemplate.reimbursementPercentage}}% reimbursement of
          </label>

          {{#if (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")}}
            <Input
              id="cc-pre-percentage-total"
              data-test-pre-percentage-cc-total
              @value={{String (divide (or this.prePercentageCourseCashTotal 0) 100)}}
              @onChange={{this.updateApprovedCourseCashTotalCalculation}}
              {{inputmask
                alias="currency"
                prefix="$"
                unmaskAsNumber=true
                digits="2"
                autoUnmask=true
              }}
              @description="Course Cash - Tuition"
              @classes={{hash base="mb-2"}}
            />

            <label for="cc-approved-total" class="font-semibold">
              {{t "tuition_assistance.program_details.status.approved_total"}}
            </label>
            <Input
              data-test-pre-percentage-total
              id="cc-approved-total"
              @value={{String (divide this.editableCourseCashTotal 100)}}
              @errors={{errorsForValibotField
                "courseCashTotal"
                schemaErrors=this.formValidationErrors
              }}
              @onChange={{this.disabledInput}}
              disabled="true"
              {{inputmask
                alias="currency"
                prefix="$"
                unmaskAsNumber=true
                digits="2"
                autoUnmask=true
              }}
            />

            <Divider class="my-2 col-span-2" />
          {{/if}}

          <label for="pre-percentage-total" class="font-semibold">
            {{@tasProgramTemplate.reimbursementPercentage}}% reimbursement of
          </label>
          <Input
            data-test-application-total-input
            id="pre-percentage-total"
            @value={{String (divide this.prePercentageTotal 100)}}
            @onChange={{this.updateApprovedTotalCalculation}}
            @description={{if
              (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")
              "All other expenses"
              ""
            }}
            {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2" autoUnmask=true}}
          />

          <label for="approved-total" class="font-semibold">
            {{t "tuition_assistance.program_details.status.approved_total"}}
          </label>
          <Input
            id="approved-total"
            @value={{divide this.editableApprovedTotal 100}}
            @errors={{errorsForValibotField "total" schemaErrors=this.formValidationErrors}}
            @onChange={{this.disabledInput}}
            disabled="true"
            {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2" autoUnmask=true}}
          />
        </div>

        <Textarea
          @value={{this.reasonForEdit}}
          @onChange={{this.updateReasonForEdit}}
          @errors={{errorsForValibotField "comment" schemaErrors=this.formValidationErrors}}
          @label={{t "common.comment"}}
        />

        <ErrorMessages @showErrors={{this.didSubmit}} @error={{this.submitError}} class="text-xs" />
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.saveField.isRunning}}
          @onCancel={{this.cancel}}
          @onSubmit={{this.saveField.perform}}
        />
      </:footer>
    </Modal>
  </template>
}
